/**
 * Main App Component
 * Routing and layout configuration
 *
 * All route pages are lazy-loaded for code splitting.
 * Only the shell (layouts, auth store, theme) loads eagerly.
 */

import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import { useAuthStore } from './store/authStore';

// Layouts — eagerly loaded (shell)
import MainLayout from './components/layouts/MainLayout';
import AuthLayout from './components/layouts/AuthLayout';

// Lazy-load fallback
const PageLoader: React.FC = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
  }}>
    <Spin size="large" />
  </div>
);

// ── Lazy Pages ──────────────────────────────────────────────────────────────

const Login = React.lazy(() => import('./pages/auth/Login'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));

// Clients
const ClientList = React.lazy(() => import('./pages/clients/ClientList'));
const ClientDetail = React.lazy(() => import('./pages/clients/ClientDetail'));

// Projects
const ProjectList = React.lazy(() => import('./pages/projects/ProjectList'));
const ProjectDetail = React.lazy(() => import('./pages/projects/ProjectDetail'));

// Financials
const EstimateList = React.lazy(() => import('./pages/financials/EstimateList'));
const EstimateBuilder = React.lazy(() => import('./pages/financials/EstimateBuilder'));
const EstimateDetail = React.lazy(() => import('./pages/financials/EstimateDetail'));
const InvoiceList = React.lazy(() => import('./pages/financials/InvoiceList'));
const InvoiceBuilder = React.lazy(() => import('./pages/financials/InvoiceBuilder'));
const InvoiceDetail = React.lazy(() => import('./pages/financials/InvoiceDetail'));
const ChangeOrderList = React.lazy(() => import('./pages/financials/ChangeOrderList'));
const ChangeOrderDetail = React.lazy(() => import('./pages/financials/ChangeOrderDetail'));
const OwnerDraws = React.lazy(() => import('./pages/financials/OwnerDraws'));
const DirectPaymentList = React.lazy(() => import('./pages/financials/DirectPaymentList'));
const ProfitDashboard = React.lazy(() => import('./pages/financials/ProfitDashboard'));
const BusinessGoals = React.lazy(() => import('./pages/financials/BusinessGoals'));
const TaxPayments = React.lazy(() => import('./pages/financials/TaxPayments'));

// Tax
const ExpenseList = React.lazy(() => import('./pages/tax/ExpenseList'));
const MileageList = React.lazy(() => import('./pages/tax/MileageList'));
const TaxDashboard = React.lazy(() => import('./pages/tax/TaxDashboard'));
const HomeOfficeDeduction = React.lazy(() => import('./pages/tax/HomeOfficeDeduction'));
const DepreciableAssets = React.lazy(() => import('./pages/tax/DepreciableAssets'));
const PerDiemTracker = React.lazy(() => import('./pages/tax/PerDiemTracker'));

// Billing
const BillingEstimateList = React.lazy(() => import('./pages/billing/EstimateList'));
const BillingEstimateDetail = React.lazy(() => import('./pages/billing/EstimateDetail'));
const BillingInvoiceList = React.lazy(() => import("./pages/billing/InvoiceList"));
const BillingInvoiceDetail = React.lazy(() => import("./pages/billing/InvoiceDetail"));

// E-Signature
const EnvelopeList = React.lazy(() => import('./pages/esign/EnvelopeList'));
const EnvelopeDetail = React.lazy(() => import('./pages/esign/EnvelopeDetail'));
const SignPage = React.lazy(() => import('./pages/esign/SignPage'));

// Schedule
const CompanySchedule = React.lazy(() => import('./pages/schedule/CompanySchedule'));
const CompanyCalendar = React.lazy(() => import('./pages/schedule/CompanyCalendar'));

// Appointments
const AppointmentCalendar = React.lazy(() => import('./pages/appointments/AppointmentCalendar'));

// Payroll
const PayrollDashboard = React.lazy(() => import('./pages/payroll/PayrollDashboard'));

// Settings
const Settings = React.lazy(() => import('./pages/Settings'));
const CompanySettings = React.lazy(() => import('./pages/settings/CompanySettings'));
const UserProfile = React.lazy(() => import('./pages/settings/UserProfile'));
const PricingSettings = React.lazy(() => import('./pages/settings/PricingSettings'));

// Licensing
const LicensingPage = React.lazy(() => import('./pages/licensing/LicensingPage'));

// Financials - Expenses (unified)
const UnifiedExpenseList = React.lazy(() => import('./pages/financials/UnifiedExpenseList'));

// Reports
const ReportsIndex = React.lazy(() => import('./pages/reports/ReportsIndex'));
const ProfitLossReport = React.lazy(() => import('./pages/reports/ProfitLossReport'));
const JobProfitabilityReport = React.lazy(() => import('./pages/reports/JobProfitabilityReport'));
const AccountsReceivableReport = React.lazy(() => import('./pages/reports/AccountsReceivableReport'));
const ARAgingReport = React.lazy(() => import('./pages/reports/ARAgingReport'));
const TaxSummaryReport = React.lazy(() => import('./pages/reports/TaxSummaryReport'));
const Contractor1099Report = React.lazy(() => import('./pages/reports/Contractor1099Report'));
const CashFlowReport = React.lazy(() => import('./pages/reports/CashFlowReport'));
const EstimateAccuracyReport = React.lazy(() => import('./pages/reports/EstimateAccuracyReport'));

// Professional Estimator
const ProfessionalEstimateList = React.lazy(() => import('./pages/estimator/EstimateList'));
const ProfessionalEstimateCreate = React.lazy(() => import('./pages/estimator/EstimateCreate'));
const ProfessionalEstimateEdit = React.lazy(() => import('./pages/estimator/EstimateEdit'));
const MaterialCatalogAdmin = React.lazy(() => import('./pages/estimator/MaterialCatalogAdmin'));
const LaborRatesAdmin = React.lazy(() => import('./pages/estimator/LaborRatesAdmin'));

// Estimator V2
const EstimatorV2 = React.lazy(() => import('./pages/estimator/EstimatorV2'));
const EstimateV2Detail = React.lazy(() => import('./pages/estimator/EstimateV2Detail'));

// Business Cards
const QRCards = React.lazy(() => import('./pages/cards/QRCards'));
const PublicCard = React.lazy(() => import('./pages/cards/PublicCard'));
const ShowQR = React.lazy(() => import('./pages/cards/ShowQR'));

const TodayBoard = React.lazy(() => import('./pages/field/TodayBoard'));
const ProjectFeed = React.lazy(() => import('./pages/field/ProjectFeed'));
const WorkerList = React.lazy(() => import('./pages/field/WorkerList'));
const SiteManagement = React.lazy(() => import('./pages/field/SiteManagement'));
const FieldAlerts = React.lazy(() => import('./pages/field/Alerts'));
const PayrollReport = React.lazy(() => import('./pages/field/PayrollReport'));
const PlanningBoard = React.lazy(() => import('./pages/field/PlanningBoard'));

// AI Chat Widget
const ClaudeChatWidgetLazy = React.lazy(() =>
  import('./components/chat').then(m => ({ default: m.ClaudeChatWidget }))
);

// Theme configuration
const theme = {
  token: {
    colorPrimary: '#1890ff',
    borderRadius: 6,
    fontSize: 14,
  },
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Chat Widget Wrapper - Only shows when authenticated
const ChatWidgetWrapper: React.FC = () => {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <ClaudeChatWidgetLazy />
    </Suspense>
  );
};

const App: React.FC = () => {
  const { checkAuth, isLoading } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <ConfigProvider theme={theme}>
      <Router>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Auth Routes */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
            </Route>

            {/* Protected Routes */}
            <Route
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<Dashboard />} />

              {/* Client Routes */}
              <Route path="/clients" element={<ClientList />} />
              <Route path="/clients/:id" element={<ClientDetail />} />

              {/* Project Routes */}
              <Route path="/projects" element={<ProjectList />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />

                          {/* Billing Routes */}
              <Route path="/billing/estimates" element={<BillingEstimateList />} />
              <Route path="/billing/estimates/:id" element={<BillingEstimateDetail />} />
              <Route path="/billing/invoices" element={<BillingInvoiceList />} />
              <Route path="/billing/invoices/:id" element={<BillingInvoiceDetail />} />

              {/* E-Signature Routes */}
              <Route path="/esign" element={<EnvelopeList />} />
              <Route path="/esign/:id" element={<EnvelopeDetail />} />

              {/* Schedule Routes */}
              <Route path="/schedule" element={<CompanySchedule />} />
              <Route path="/calendar" element={<CompanyCalendar />} />

              {/* Appointment Routes */}
              <Route path="/appointments" element={<AppointmentCalendar />} />

              {/* Financial Routes */}
              <Route path="/estimates" element={<EstimateList />} />
              <Route path="/estimates/new" element={<EstimateBuilder />} />
              <Route path="/estimates/:id" element={<EstimateDetail />} />
              <Route path="/estimates/:id/edit" element={<EstimateBuilder />} />
              <Route path="/invoices" element={<InvoiceList />} />
              <Route path="/invoices/new" element={<InvoiceBuilder />} />
              <Route path="/invoices/:id" element={<InvoiceDetail />} />
              <Route path="/change-orders" element={<ChangeOrderList />} />
              <Route path="/change-orders/:id" element={<ChangeOrderDetail />} />
              <Route path="/financials/expenses" element={<UnifiedExpenseList />} />
              <Route path="/financials/owner-draws" element={<OwnerDraws />} />
              <Route path="/financials/direct-payments" element={<DirectPaymentList />} />
              <Route path="/financials/profit" element={<ProfitDashboard />} />
              <Route path="/financials/business-goals" element={<BusinessGoals />} />
              <Route path="/financials/tax-payments" element={<TaxPayments />} />

              {/* Professional Estimator Routes */}
              <Route path="/estimator" element={<ProfessionalEstimateList />} />
              <Route path="/estimator/create" element={<ProfessionalEstimateCreate />} />
              <Route path="/estimator/:id" element={<ProfessionalEstimateEdit />} />
              <Route path="/estimator/:id/edit" element={<ProfessionalEstimateEdit />} />
              <Route path="/estimator/materials" element={<MaterialCatalogAdmin />} />
              <Route path="/estimator/labor-rates" element={<LaborRatesAdmin />} />

              {/* Estimator V2 Routes */}
              <Route path="/estimator-v2" element={<EstimatorV2 />} />
              <Route path="/estimator-v2/:id" element={<EstimateV2Detail />} />

              {/* Tax Routes */}
              <Route path="/tax/expenses" element={<ExpenseList />} />
              <Route path="/tax/mileage" element={<MileageList />} />
              <Route path="/tax/dashboard" element={<TaxDashboard />} />
              <Route path="/tax/home-office" element={<HomeOfficeDeduction />} />
              <Route path="/tax/depreciation" element={<DepreciableAssets />} />
              <Route path="/tax/per-diem" element={<PerDiemTracker />} />

              {/* Payroll Routes */}
              <Route path="/payroll" element={<PayrollDashboard />} />

              {/* Licensing */}
              <Route path="/licensing" element={<LicensingPage />} />

              {/* Report Routes */}
              <Route path="/reports" element={<ReportsIndex />} />
              <Route path="/reports/profit-loss" element={<ProfitLossReport />} />
              <Route path="/reports/job-profitability" element={<JobProfitabilityReport />} />
              <Route path="/reports/accounts-receivable" element={<AccountsReceivableReport />} />
              <Route path="/reports/ar-aging" element={<ARAgingReport />} />
              <Route path="/reports/tax-summary" element={<TaxSummaryReport />} />
              <Route path="/reports/1099-contractors" element={<Contractor1099Report />} />
              <Route path="/reports/cash-flow" element={<CashFlowReport />} />
              <Route path="/reports/estimate-accuracy" element={<EstimateAccuracyReport />} />

                <Route path="/field" element={<TodayBoard />} />
                <Route path="/field/project/:id" element={<ProjectFeed />} />
                <Route path="/field/workers" element={<WorkerList />} />
                <Route path="/field/sites" element={<SiteManagement />} />
                <Route path="/field/alerts" element={<FieldAlerts />} />
                <Route path="/field/payroll" element={<PayrollReport />} />
                <Route path="/field/planning" element={<PlanningBoard />} />

              {/* QR & Business Cards */}
              <Route path="/qr" element={<QRCards />} />

              {/* Settings Routes */}
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/company" element={<CompanySettings />} />
              <Route path="/settings/profile" element={<UserProfile />} />
              <Route path="/settings/pricing" element={<PricingSettings />} />
            </Route>

            {/* Public E-Signature Route (no auth) */}
            <Route path="/sign/:token" element={<SignPage />} />

            {/* Show QR — full-screen, no auth, no chrome, works offline */}
            <Route path="/qr/show" element={<ShowQR />} />

            {/* Public Business Card (no auth, no chrome) */}
            <Route path="/card/:slug" element={<PublicCard />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>

        {/* AI Chat Widget - Available on all pages for authenticated users */}
        <ChatWidgetWrapper />
      </Router>
    </ConfigProvider>
  );
};

export default App;
